import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';

export const CCS_TYPES = new Set(['combo_typ2', 'tesla_supercharger_ccs']);
function isCCS(type) { return CCS_TYPES.has(type); }

function project(loc) {
  return fromLonLat([loc.coordinates.lng, loc.coordinates.lat]);
}

function dist2d(a, b) {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return Math.sqrt(dx * dx + dy * dy);
}

function networkPrefix(loc) {
  if (loc.network === 'IONITY')             return 'i';
  if (loc.network === 'Aral pulse')         return 'a';
  if (loc.network === 'EnBW')               return 'e';
  if (loc.network === 'EWE Go')             return 'w';
  if (loc.network === 'Tesla Supercharger') return 'T';
  return '';
}

export function buildFeatures(chargelocations, zoom, minPoints, minLoad, filterProblems) {
  let maxDist = 8192;
  for (let i = 10; i < zoom; i++)
    maxDist = maxDist / 2;

  // --- split into primary (meets minPoints) and secondary (meets minKW only) ---
  const candidates = [];
  const secondary = [];
  for (const loc of chargelocations) {
    if (filterProblems && loc.fault_report) continue;
    let cnt = 0;
    if (loc.chargepoints) {
      for (const pnt of loc.chargepoints)
        if (pnt.power >= minLoad && isCCS(pnt.type)) cnt += pnt.count;
    } else {
      cnt = 999;
    }
    if (cnt < 1) continue;

    const xy = project(loc);
    if (cnt >= (zoom >= 14 ? 1 : minPoints)) {
      const prefix = cnt === 999 ? '' : networkPrefix(loc);
      candidates.push({ loc, cnt, marker: cnt === 999 ? '' : prefix + cnt, xy, merged: false });
    } else {
      secondary.push({ loc, xy });
    }
  }

  // --- grid-bucketed merge: O(n) average vs O(n²) ---
  // Each cell covers maxDist × maxDist projected metres.
  // For every candidate we only check the 3×3 neighbourhood of cells.
  const cellSize = maxDist;
  const grid = new Map(); // key "cx,cy" → candidate

  function cellKey(cx, cy) { return cx + ',' + cy; }

  for (const item of candidates) {
    const cx = Math.floor(item.xy[0] / cellSize);
    const cy = Math.floor(item.xy[1] / cellSize);

    let target = null;
    outer: for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const neighbour = grid.get(cellKey(cx + dx, cy + dy));
        if (neighbour && dist2d(item.xy, neighbour.xy) < maxDist) {
          target = neighbour;
          break outer;
        }
      }
    }

    if (target) {
      target.marker = target.marker + '+' + item.marker;
      target.cnt += item.cnt;
      target.locs.push(item.loc);
      item.merged = true;
    } else {
      item.locs = [item.loc];
      grid.set(cellKey(cx, cy), item);
    }
  }

  // --- attach secondary stations to nearest marker within maxDist ---
  // (at zoom >= 14 all qualifying stations are primary, so secondary is empty)
  for (const sec of secondary) {
    const cx = Math.floor(sec.xy[0] / cellSize);
    const cy = Math.floor(sec.xy[1] / cellSize);
    outer: for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const marker = grid.get(cellKey(cx + dx, cy + dy));
        if (marker && dist2d(sec.xy, marker.xy) < maxDist) {
          marker.locs.push(sec.loc);
          break outer;
        }
      }
    }
  }

  // --- build OL features ---
  return candidates
    .filter(item => !item.merged)
    .map(item => {
      const feature = new Feature({
        geometry: new Point(item.xy),
        name: item.marker,
      });
      feature.properties = { cnt: item.marker, locs: item.locs };
      return feature;
    });
}
